import AppRoutes from '@routes/index'
import './index.css'
import './styles/fonts.css';
import { AuthProvider } from '@contexts/AuthContext';
import { useEffect } from 'react';
import { useLanguageStore } from '@stores/languageStore';
import {
  Biometrics,
  disableSwipeClose,
  expandScreen,
  enableClosingConfirmation,
  lockOrientation,
  getPlatform,
  close,
  openLink,
} from './lib/telegram';
import * as Sentry from "@sentry/react";

if (import.meta.env.VITE_ENV === 'production') {
  const originalLog = console.log;
  console.log = function (...args) {
    const message = String(args[0] || '');

    // Filter out event logs
    if (message.includes('postEvent') || message.includes('receiveEvent') ||
      message.includes('[Telegram.WebApp]')) {
      return;
    }

    originalLog.apply(console, args);
  };
}

if (getPlatform() === 'unknown') {
  // prompt to open telegram app
  enableClosingConfirmation(false)
  window.location.href = import.meta.env.VITE_TELEGRAM_BOT_LINK
} else if (getPlatform() === 'weba') {
  enableClosingConfirmation(false)
  openLink(import.meta.env.VITE_TELEGRAM_BOT_LINK)
  close()
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV,
  debug: true,
  beforeSend(event) {
    // List of sensitive keys that should never be logged
    const sensitiveKeys = [
      'mnemonic', 'ton_mnemonic', 'secret', 'secretKey', 'ton_secret_key',
      'pin', 'ton_pin', 'token', 'ton_token', 'privateKey',
      'private_key', 'signature', 'password', 'passphrase', 'seed',
      // API headers and authentication
      'authorization', 'x-api-key', 'x-token', 'x-auth-token', 'api-key',
      // User identifiers
      'telegram_id', 'user_id', 'wallet_address'
    ];

    // Check if a string value looks like a bearer token
    const isBearerToken = (value) => {
      if (typeof value !== 'string') return false;
      return value.toLowerCase().includes('bearer ');
    };

    // Function to recursively scrub sensitive data
    const scrubSensitiveData = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;

      if (Array.isArray(obj)) {
        return obj.map(item => scrubSensitiveData(item));
      }

      const scrubbed = {};
      for (const [key, value] of Object.entries(obj)) {
        const lowerKey = key.toLowerCase();
        // Check if key contains any sensitive keywords
        const isSensitive = sensitiveKeys.some(sensitiveKey =>
          lowerKey.includes(sensitiveKey.toLowerCase())
        );

        if (isSensitive) {
          scrubbed[key] = '[Filtered]';
        } else if (isBearerToken(value)) {
          // Check if value contains a bearer token
          scrubbed[key] = '[Filtered]';
        } else if (typeof value === 'object' && value !== null) {
          scrubbed[key] = scrubSensitiveData(value);
        } else {
          scrubbed[key] = value;
        }
      }
      return scrubbed;
    };

    // Scrub breadcrumbs
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map(breadcrumb => ({
        ...breadcrumb,
        data: scrubSensitiveData(breadcrumb.data),
        message: breadcrumb.message // Keep messages as-is since they're usually safe
      }));
    }

    // Scrub request data
    if (event.request) {
      event.request = scrubSensitiveData(event.request);
    }

    // Scrub contexts
    if (event.contexts) {
      event.contexts = scrubSensitiveData(event.contexts);
    }

    // Scrub extra data
    if (event.extra) {
      event.extra = scrubSensitiveData(event.extra);
    }

    // Scrub tags
    if (event.tags) {
      event.tags = scrubSensitiveData(event.tags);
    }

    // Scrub user data
    if (event.user) {
      event.user = scrubSensitiveData(event.user);
    }

    return event;
  }
});

function App() {
  const { language } = useLanguageStore();

  useEffect(() => {
    // Initialize language from store
    useLanguageStore.getState().changeLanguage(language);

    const init = async () => {
      await Biometrics.biometricsInit()

      expandScreen()
      disableSwipeClose()
      lockOrientation()
      enableClosingConfirmation()

      if (Biometrics.isBiometricsInited() && (!Biometrics.isAccessGranted() || !Biometrics.IsTokenSaved())) {
        Biometrics.requestBiometricsAccess()
      }
    }

    init()
  }, [language]);

  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}

const SentryApp = Sentry.wrapUseRoutesV7(App)
export default SentryApp